/**
 * Care Type options eligible as a facility license for Policies (Phase 3).
 * Search specialties (service_tag) stay out of registration / profile business_type.
 */

import {
  BUSINESS_TYPE_REGISTRATION_OPTIONS,
  REVERSE_SPECIALIZATION_MAPPINGS,
} from "../generated/careTaxonomy";
import {
  CARE_TYPE_LICENSE_MAP_BY_KEY,
  lookupCareTypeLicenseEntry,
  normalizeCareTypeKey,
  type CareTypeLicenseMapEntry,
  type CareTypePolicyClass,
} from "./careTypeLicenseMap";
import { formatLicenseTypeLabel } from "./missingFieldLabels";
import type { PolicyPackAccess } from "./types";

const FACILITY_LICENSE_CLASSES: ReadonlySet<CareTypePolicyClass> = new Set([
  "license_primary",
  "license_alias",
  "deferred_pack",
]);

/** Compact / legacy tokens → map keys (aligns FE with BE taxonomy normalization). */
const CARE_TYPE_KEY_ALIASES: Record<string, string> = {
  assistedliving: "assisted_living",
  homecare: "home_care",
  homehealth: "home_health",
  home_health_agency: "home_health",
  home_health_care: "home_care",
  homehealthcare: "home_care",
  grouphome: "group_home",
  memorycare: "memory_care",
  nursinghome: "nursing_home",
  skillednursing: "skilled_nursing",
  adultdaycare: "adult_day_care",
  independentliving: "independent_living",
  respitecare: "respite_care",
  palliativecare: "palliative_care",
  woundcare: "wound_care",
};

/**
 * Resolve raw Care Type / business_type / display label to a matrix map key.
 * Mirrors BE normalize_business_type_key + care-type normalize for common cases.
 */
export function resolveCareTypeMapKey(raw?: string | null): string {
  const trimmed = (raw || "").trim();
  if (!trimmed) return "";

  const fromLabel = REVERSE_SPECIALIZATION_MAPPINGS[trimmed];
  if (fromLabel && CARE_TYPE_LICENSE_MAP_BY_KEY[fromLabel]) {
    return fromLabel;
  }

  const key = normalizeCareTypeKey(trimmed);
  if (CARE_TYPE_LICENSE_MAP_BY_KEY[key]) return key;

  const compact = key.replace(/_/g, "");
  const aliased = CARE_TYPE_KEY_ALIASES[key] || CARE_TYPE_KEY_ALIASES[compact];
  if (aliased && CARE_TYPE_LICENSE_MAP_BY_KEY[aliased]) return aliased;

  if (key.endsWith("_agency")) {
    const base = key.replace(/_agency$/, "");
    if (CARE_TYPE_LICENSE_MAP_BY_KEY[base]) return base;
  }

  return key;
}

export function lookupResolvedCareTypeLicenseEntry(
  raw?: string | null
): CareTypeLicenseMapEntry | undefined {
  const key = resolveCareTypeMapKey(raw);
  if (!key) return undefined;
  return (
    CARE_TYPE_LICENSE_MAP_BY_KEY[key] || lookupCareTypeLicenseEntry(key)
  );
}

/** Care Types that may be stored as Provider.business_type for Policies. */
export function isFacilityLicenseCareType(
  key: string | null | undefined
): boolean {
  const entry = lookupResolvedCareTypeLicenseEntry(key);
  if (!entry) return false;
  return FACILITY_LICENSE_CLASSES.has(entry.class);
}

/** Registration / profile dropdown: exclude service_tag and invalid keys. */
export const POLICY_FACILITY_LICENSE_REGISTRATION_OPTIONS =
  BUSINESS_TYPE_REGISTRATION_OPTIONS.filter((opt) =>
    isFacilityLicenseCareType(opt.value)
  );

export const CARE_TYPE_LICENSE_HELP =
  "Choose the facility’s regulated license type (e.g. Assisted Living, Home Care). Memory Care maps to assisted living with dementia care. Service specialties such as Wound Care are selected separately in search/profile capabilities — not as the primary Care Type.";

const OVERLAY_LABELS: Record<string, string> = {
  minnesota_assisted_living: "MN 144G overlay",
  minnesota_home_care: "MN 144A overlay",
  minnesota_group_home: "MN 245D overlay",
  minnesota_hospice: "MN 144A.75 overlay",
};

const FAMILY_LABELS: Record<string, string> = {
  assisted_living: "Assisted living",
  home_care: "Home care",
  group_home: "Group home",
  hospice: "Hospice",
  nursing_home: "Nursing home",
  adult_day_care: "Adult day care",
  independent_living: "Independent living",
  rehabilitation: "Rehabilitation",
};

function compareKey(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

/**
 * Policies pack license family (+ MN overlay only when facility is in Minnesota).
 * Example (MN HC): "Home care · MN 144A overlay"
 * Example (non-MN HC): "Home care"
 */
export function formatPolicyPackLicenseLabel(
  pack?: PolicyPackAccess | null,
  options?: { includeMinnesota?: boolean }
): string {
  if (!pack) return "";
  const family = (pack.license_family || "").trim();
  if (!family || family === "unknown") return "";
  const familyLabel =
    FAMILY_LABELS[family] ||
    family
      .split("_")
      .filter(Boolean)
      .map((w, i) =>
        i === 0
          ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
          : w.toLowerCase()
      )
      .join(" ");
  const overlayKey = pack.jurisdiction_overlay || "";
  const overlay = OVERLAY_LABELS[overlayKey];
  if (
    overlay &&
    pack.available !== false &&
    options?.includeMinnesota === true
  ) {
    return `${familyLabel} · ${overlay}`;
  }
  return familyLabel;
}

/**
 * Single Policies header license segment: avoid "Home Care · Home care · MN 144A".
 * Keep Care Type when it differs from pack family (e.g. Memory Care → Assisted living).
 */
export function formatPoliciesHeaderLicenseSegment(options: {
  licenseType?: string | null;
  pack?: PolicyPackAccess | null;
  includeMinnesota?: boolean;
}): string {
  const careTypeLabel = formatLicenseTypeLabel(options.licenseType);
  const packLabel = formatPolicyPackLicenseLabel(options.pack, {
    includeMinnesota: options.includeMinnesota,
  });
  if (!packLabel) return careTypeLabel;
  if (!careTypeLabel) return packLabel;

  const packFamilyPart = packLabel.split(" · ")[0] || packLabel;
  const careKey = compareKey(careTypeLabel);
  const packKey = compareKey(packFamilyPart);
  if (
    careKey === packKey ||
    careKey.includes(packKey) ||
    packKey.includes(careKey)
  ) {
    return packLabel;
  }
  return `${careTypeLabel} · ${packLabel}`;
}
